/* Shared carousel for the flavour sections.

   How many cards fit is decided in CSS by --carousel-per; this only reads that
   number. Without data-carousel-loop it pages by whole screens and wraps at
   both ends with an unanimated jump. With it, it moves one card at a time and
   cloned cards flank the originals so the slide never runs out. */
(() => {
  const init = (root) => {
    if (root.dataset.carouselReady) return;

    const track = root.querySelector('[data-carousel-track]');
    const nav = root.querySelector('[data-carousel-nav]');
    const prev = root.querySelector('[data-carousel-prev]');
    const next = root.querySelector('[data-carousel-next]');
    const count = root.querySelector('[data-carousel-count]');
    if (!track || !nav || !prev || !next) return;

    const items = Array.from(track.children);
    if (items.length < 2) {
      // Arrows that cannot go anywhere read as a broken control, not as an end.
      nav.hidden = true;
      return;
    }

    const total = items.length;
    const loop = root.hasAttribute('data-carousel-loop');
    const mod = (i) => ((i % total) + total) % total;

    let page = 0;
    // Loop mode: first visible original; leaves 0..total-1 only while a slide runs.
    let pos = 0;
    let clones = [];

    const perView = () => {
      const raw = parseInt(getComputedStyle(root).getPropertyValue('--carousel-per'), 10);
      return Number.isFinite(raw) && raw > 0 ? raw : 1;
    };

    // The desktop layouts drop back to a plain grid, where paging must not run.
    const isActive = () => getComputedStyle(track).display === 'flex';

    const makeClone = (item) => {
      const clone = item.cloneNode(true);
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      clone.removeAttribute('id');
      clone.dataset.carouselClone = '';
      return clone;
    };

    // One set before is enough since per < total; two after cover pos + 2 * per.
    const setClones = (on) => {
      if (on && !clones.length) {
        const before = items.map(makeClone);
        const after = [...items, ...items].map(makeClone);
        track.prepend(...before);
        track.append(...after);
        clones = [...before, ...after];
      } else if (!on && clones.length) {
        clones.forEach((clone) => clone.remove());
        clones = [];
      }
    };

    // Off-screen cards stay out of the tab order and the accessibility tree.
    const setVisible = (first, per) => {
      Array.from(track.children).forEach((card, i) => {
        card.inert = first !== null && (i < first || i >= first + per);
      });
    };

    const setX = (x, jumped) => {
      if (jumped) track.setAttribute('data-jump', '');
      root.style.setProperty('--carousel-x', `${x}px`);
      if (jumped) {
        void track.offsetWidth; // flush before the transition is allowed back
        track.removeAttribute('data-jump');
      }
    };

    const reset = () => {
      setClones(false);
      setVisible(null, 0);
      nav.hidden = true;
      setX(0, true);
    };

    const render = (jumped) => {
      const per = perView();
      if (!isActive() || total <= per) {
        reset();
        return;
      }

      nav.hidden = false;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const step = items[0].getBoundingClientRect().width + gap;

      let first;
      if (loop) {
        setClones(true);
        first = pos + total;
        if (count) count.textContent = `${mod(pos) + 1} / ${total}`;
      } else {
        const pages = Math.ceil(total / per);
        page = Math.min(page, pages - 1);
        // Last page sits flush right so a short remainder never leaves empty slots.
        first = Math.min(page * per, total - per);
        if (count) count.textContent = `${page + 1} / ${pages}`;
      }

      setX(-first * step, jumped);
      setVisible(first, per);
    };

    const settle = () => {
      if (pos >= 0 && pos < total) return;
      pos = mod(pos);
      render(true);
    };

    const isAnimated = () => parseFloat(getComputedStyle(track).transitionDuration) > 0;

    const go = (delta) => {
      const per = perView();

      if (loop) {
        settle(); // a click mid-slide must not run past the clones
        pos += delta;
        render(false);
        if (!isAnimated()) settle();
        return;
      }

      const pages = Math.max(1, Math.ceil(total / per));
      const target = page + delta;
      const wrapped = target < 0 || target > pages - 1;

      page = (target + pages) % pages;
      render(wrapped);
    };

    track.addEventListener('transitionend', (event) => {
      if (loop && event.target === track) settle();
    });
    prev.addEventListener('click', () => go(-1));
    next.addEventListener('click', () => go(1));
    window.addEventListener('resize', () => render(true));

    root.dataset.carouselReady = 'true';
    render(true);
  };

  const scan = (scope) => scope.querySelectorAll('[data-carousel]').forEach(init);

  scan(document);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
